import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContentText,
  DialogContent,
  Paper,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Delete,
  UploadFile,
  FileUploadOutlined,
  OpenInNew,
  AddAPhotoOutlined,
  InsertDriveFileOutlined,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import {
  getDocumentosByFabrica,
  uploadDocumentosFabricaMultiples,
  deleteDocumentoFabrica,
} from "../../api/ProveedoresyFabricas/documentosFabricasApi";

const ACCENT = "#ffac7e";
const DEFAULT_DOC_TYPE = "Factory Document";

const toLocalISODate = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const isImageFile = (archivo) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(archivo || ""));

const ProveedoresFabricasDocumentos = ({ fabrica }) => {
  const theme = useTheme();
  const [documentos, setDocumentos] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fechaCaducidad, setFechaCaducidad] = useState(toLocalISODate());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [dropActive, setDropActive] = useState(false);
  const [previewDialogSrc, setPreviewDialogSrc] = useState(null);
  const [cameraInputId] = useState(
    () => `factory-camera-upload-${Math.random().toString(36).slice(2, 9)}`,
  );

  useEffect(() => {
    if (fabrica && fabrica.id_Fabrica) {
      fetchDocumentos();
    }
  }, [fabrica]);

  const fetchDocumentos = async () => {
    try {
      const data = await getDocumentosByFabrica(fabrica.id_Fabrica);
      setDocumentos(data);
    } catch (error) {
      console.error("❌ Error al obtener documentos:", error);
    }
  };

  const addFiles = (filesLike) => {
    const files = Array.from(filesLike || []);
    if (files.length === 0) return;
    setSelectedFiles((prev) => {
      const existing = new Set(
        prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`),
      );
      const next = [...prev];
      files.forEach((f) => {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        if (!existing.has(key)) {
          existing.add(key);
          next.push(f);
        }
      });
      return next;
    });
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0 || !fechaCaducidad) {
      alert("Debes seleccionar al menos un archivo y fecha de caducidad.");
      return;
    }

    try {
      await uploadDocumentosFabricaMultiples(
        selectedFiles,
        fabrica.id_Fabrica,
        DEFAULT_DOC_TYPE,
        fechaCaducidad,
      );
      setSelectedFiles([]);
      setFechaCaducidad(toLocalISODate());
      fetchDocumentos();
    } catch (error) {
      console.error("❌ Error al subir documento:", error);
    }
  };

  const handleDeleteDocumento = async () => {
    if (!confirmDelete) return;
    try {
      await deleteDocumentoFabrica(confirmDelete);
      fetchDocumentos();
    } catch (error) {
      console.error("❌ Error al eliminar documento:", error);
    }
    setConfirmDelete(null);
  };

  return (
    <Paper
      sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        borderRadius: 2,
        mt: 2,
        p: 2,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.2 }}>
        Factory documents
      </Typography>

      {documentos.length === 0 ? (
        <Typography color="textSecondary" variant="body2">
          No documents uploaded
        </Typography>
      ) : (
        <List sx={{ py: 0 }}>
          {documentos.map((doc) => (
            (() => {
              const url = `${process.env.REACT_APP_API_URL}/uploads/fabricas/${doc.Archivo}`;
              const image = isImageFile(doc.Archivo);
              return (
            <ListItem
              key={doc.id_Documento}
              sx={{
                px: 0,
                py: 0.75,
                borderBottom: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
                alignItems: "flex-start",
              }}
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      },
                    }}
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{
                      border: `1px solid ${alpha(theme.palette.error.main, 0.45)}`,
                      color: theme.palette.error.main,
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.error.main, 0.12),
                      },
                    }}
                    onClick={() => setConfirmDelete(doc.id_Documento)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ pr: 11 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 1.2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    overflow: "hidden",
                    flexShrink: 0,
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {image ? (
                    <Box
                      component="img"
                      src={url}
                      alt=""
                      onClick={() => setPreviewDialogSrc(url)}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        cursor: "zoom-in",
                      }}
                    />
                  ) : (
                    <InsertDriveFileOutlined sx={{ color: "text.secondary" }} />
                  )}
                </Box>
                <ListItemText
                  primary={doc.TipoDocumento || DEFAULT_DOC_TYPE}
                  secondary={doc.Archivo}
                />
              </Stack>
            </ListItem>
              );
            })()
          ))}
        </List>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.2 }}>
          Upload date: {new Date(fechaCaducidad).toLocaleDateString()}
        </Typography>

        <Box
          onDragEnter={(e) => {
            e.preventDefault();
            setDropActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            if (!e.currentTarget.contains(e.relatedTarget)) setDropActive(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDropActive(false);
            addFiles(e.dataTransfer.files);
          }}
          sx={{
            p: 2,
            borderRadius: 2,
            border: dropActive
              ? `2px dashed ${ACCENT}`
              : `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
            backgroundColor: dropActive
              ? alpha(ACCENT, 0.14)
              : alpha(theme.palette.text.primary, 0.02),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
            <FileUploadOutlined sx={{ color: ACCENT }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Drag & drop documents here
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            You can select multiple files, upload all at once, or take a photo from your phone.
          </Typography>

          {selectedFiles.length > 0 && (
            <Stack
              direction="row"
              spacing={0.6}
              sx={{ mt: 1.2, flexWrap: "wrap", rowGap: 0.6 }}
            >
              {selectedFiles.map((f) => (
                <Chip
                  key={`${f.name}-${f.size}-${f.lastModified}`}
                  label={f.name}
                  size="small"
                  onDelete={() =>
                    setSelectedFiles((prev) =>
                      prev.filter(
                        (x) =>
                          `${x.name}-${x.size}-${x.lastModified}` !==
                          `${f.name}-${f.size}-${f.lastModified}`,
                      ),
                    )
                  }
                />
              ))}
            </Stack>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <input
            type="file"
            multiple
            onChange={(e) => addFiles(e.target.files)}
            style={{ display: "none" }}
            id="factory-files-upload"
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => addFiles(e.target.files)}
            style={{ display: "none" }}
            id={cameraInputId}
          />
          <label htmlFor="factory-files-upload" style={{ flex: 1 }}>
            <Tooltip title="Select files">
              <Button
                fullWidth
                variant="outlined"
                component="span"
                aria-label="Select files"
                sx={{
                  minHeight: 56,
                  minWidth: 56,
                  borderColor: alpha(ACCENT, 0.7),
                  color: theme.palette.text.primary,
                  "&:hover": {
                    borderColor: ACCENT,
                    backgroundColor: alpha(ACCENT, 0.1),
                  },
                }}
              >
                <UploadFile />
              </Button>
            </Tooltip>
          </label>
          <label htmlFor={cameraInputId} style={{ flex: 1 }}>
            <Tooltip title="Take photo">
              <Button
                fullWidth
                variant="outlined"
                component="span"
                aria-label="Take photo"
                sx={{
                  minHeight: 56,
                  minWidth: 56,
                  borderColor: alpha(ACCENT, 0.7),
                  color: theme.palette.text.primary,
                  "&:hover": {
                    borderColor: ACCENT,
                    backgroundColor: alpha(ACCENT, 0.1),
                  },
                }}
              >
                <AddAPhotoOutlined />
              </Button>
            </Tooltip>
          </label>
          <Tooltip title="Upload all">
            <span style={{ flex: 1, display: "flex" }}>
              <Button
                fullWidth
                sx={{
                  minHeight: 56,
                  minWidth: 56,
                  bgcolor: ACCENT,
                  color: theme.palette.getContrastText(ACCENT),
                  "&:hover": { bgcolor: alpha(ACCENT, 0.9) },
                }}
                variant="contained"
                onClick={handleFileUpload}
                disabled={selectedFiles.length === 0}
                aria-label="Upload all"
              >
                <FileUploadOutlined />
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Eliminar Documento</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que deseas eliminar este documento? Esta acción no
            se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteDocumento} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(previewDialogSrc)}
        onClose={() => setPreviewDialogSrc(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0,0,0,0.82)" } },
        }}
        PaperProps={{
          sx: {
            bgcolor: "rgba(18,18,18,0.98)",
            backgroundImage: "none",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <DialogContent
          sx={{
            position: "relative",
            p: { xs: 1, sm: 2 },
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setPreviewDialogSrc(null)}
            aria-label="Close photo"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              color: "common.white",
              zIndex: 1,
              bgcolor: "rgba(255,255,255,0.14)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          {previewDialogSrc ? (
            <Box
              component="img"
              src={previewDialogSrc}
              alt=""
              sx={{
                maxWidth: "100%",
                maxHeight: "min(85vh, 920px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: 1,
                display: "inline-block",
                mt: 0.5,
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default ProveedoresFabricasDocumentos;
